import { useEffect, useRef, useState } from "react";
import api, { API, getToken, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" });
  } catch {
    return "";
  }
}

function MessageBubble({ msg, isMine }) {
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} mb-2`}>
      <div
        className={`max-w-[75%] px-3 py-2 border ${
          isMine ? "bg-black text-white border-black" : "bg-white text-black border-zinc-300"
        }`}
        data-testid={`message-${msg.id}`}
      >
        {!isMine && (
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">
            {msg.sender_name} {msg.sender_role !== "driver" && "· Supervisor"}
          </div>
        )}
        <div className="whitespace-pre-wrap text-sm">{msg.text}</div>
        <div className={`text-[10px] mt-1 ${isMine ? "text-zinc-300" : "text-zinc-500"}`}>
          {formatTime(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

function ChatThread({ driverId, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const params = driverId ? { driver_id: driverId } : {};
      const { data } = await api.get("/chat/messages", { params });
      setMessages(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [driverId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const params = driverId ? { driver_id: driverId } : {};
      const { data } = await api.post("/chat/messages", { text }, { params });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-zinc-50" data-testid="messages-container">
        {messages.length === 0 ? (
          <div className="text-center text-zinc-500 text-sm py-10">
            Aún no hay mensajes. Escribe el primero abajo.
          </div>
        ) : (
          messages.map((m) => (
            <MessageBubble key={m.id} msg={m} isMine={m.sender_id === currentUser.id} />
          ))
        )}
      </div>
      <form onSubmit={submit} className="flex gap-2 p-3 border-t border-black bg-white">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribe un mensaje…"
          className="rounded-none border-black h-11"
          data-testid="chat-input"
        />
        <Button
          type="submit"
          disabled={sending || !text.trim()}
          className="rounded-none btn-primary h-11 px-5"
          data-testid="chat-send-btn"
        >
          {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        </Button>
      </form>
    </div>
  );
}

export default function ChatPage() {
  const { user } = useAuth();
  const isDriver = user?.role === "driver";
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/chat/threads");
      setThreads(data);
      if (!selected && data.length > 0) setSelected(data[0].driver_id);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!isDriver) {
      loadThreads();
      const t = setInterval(loadThreads, 10000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line
  }, [isDriver]);

  if (isDriver) {
    return (
      <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-3xl mx-auto h-[calc(100vh-2rem)] flex flex-col" data-testid="chat-page-driver">
        <header className="mb-4">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Chat</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Chat con supervisor
          </h1>
          <p className="text-sm text-zinc-600 mt-1">
            Cualquier admin/staff del centro de control verá y responderá tus mensajes.
          </p>
        </header>
        <div className="border border-black flex-1 overflow-hidden flex flex-col bg-white min-h-0">
          <ChatThread currentUser={user} />
        </div>
      </div>
    );
  }

  // Staff view
  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto" data-testid="chat-page-staff">
      <header className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Comunicación
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
          Chat con conductores
        </h1>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black h-[70vh] bg-white">
        {/* Threads list */}
        <aside className="md:col-span-1 border-r border-black overflow-y-auto" data-testid="threads-list">
          {threads.length === 0 ? (
            <div className="p-4 text-sm text-zinc-500">Sin conductores.</div>
          ) : threads.map((t) => {
            const photo = t.has_photo
              ? `${API}/users/${t.driver_id}/photo?auth=${encodeURIComponent(getToken())}`
              : null;
            return (
              <button
                key={t.driver_id}
                onClick={() => setSelected(t.driver_id)}
                className={`w-full text-left px-4 py-3 border-b border-zinc-200 flex items-center gap-3 hover:bg-zinc-50 ${
                  selected === t.driver_id ? "bg-zinc-100" : ""
                }`}
                data-testid={`thread-${t.driver_id}`}
              >
                <div className="w-10 h-10 border border-black bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0">
                  {photo ? (
                    <img src={photo} alt={t.driver_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-bold text-zinc-400">
                      {t.driver_name?.slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm truncate">{t.driver_name}</div>
                    {t.unread > 0 && (
                      <span className="bg-[#E63946] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                        {t.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {t.last_message?.text || "Sin mensajes"}
                  </div>
                </div>
              </button>
            );
          })}
        </aside>
        {/* Active thread */}
        <div className="md:col-span-2 flex flex-col min-h-0">
          {selected ? (
            <ChatThread key={selected} driverId={selected} currentUser={user} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-zinc-500">
              <div className="text-center">
                <MessageSquare size={40} className="mx-auto mb-3 text-zinc-300" />
                <p className="text-sm">Selecciona un conductor para comenzar.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
